export type MatchingFlowMode = 'upload' | 'load';

/** Org type id for brands in GET /organizations/by-orgtype. */
export const BRAND_ORG_TYPE_ID = 1;

/** A brand organization returned by /organizations/by-orgtype?orgTypeId=1. */
export interface BrandOrg {
  id: number;
  bhiveId: string;
  orgName: string;
  countryName?: string;
  subscriptionType?: string;
  /** Whether the org exists in Bhive — drives the `nonBhive` upload flag. */
  bhiveOrg?: boolean;
  /** Joined from bor_address server-side; absent when the brand has none on file. */
  address?: string | null;
  /** Trade IDs joined from bor_identification / bor_gateway_subscription. */
  higgId?: string | null;
  zdhcAid?: string | null;
  oarId?: string | null;
  ffcId?: string | null;
}

/** An organization of any type returned by /organizations/by-name — used for manual match search. */
export interface OrgSearchResult {
  bhiveId: string;
  orgName: string;
  /** Joined from bor_address server-side; absent when the org has none on file. */
  address?: string;
  bhiveOrg?: boolean;
  countryName?: string;
  /** Raw regular value — display `effectiveSubscriptionType` instead. */
  subscriptionType?: string;
  /** What to show: the admin override when set, else subscriptionType. Resolved server-side. */
  effectiveSubscriptionType?: string | null;
  /** Status resolved server-side: -1 GHOST_MERGED, 0 INACTIVE, 1 ACTIVE, 2 GHOST, 3 HIDDEN. */
  statusCode?: number;
  /** Human label for statusCode — same mapping the suggestion cards use. */
  statusLabel?: string;
  /** Trade IDs joined from bor_identification / bor_gateway_subscription. */
  higgId?: string | null;
  zdhcAid?: string | null;
  oarId?: string | null;
  ffcId?: string | null;
}

/** Query params for POST /bulk_matching/upload_excel. */
export interface UploadExcelParams {
  brandId: string;
  userName: string;
  nonBhive?: boolean;
}

/**
 * Response of upload_excel. The backend returns an opaque object; the batch
 * identifier is the field we rely on to drive the rest of the flow.
 * TODO: tighten once a real upload response is observed.
 */
export interface UploadBatchResponse {
  batchId?: string;
  message?: string;
  [key: string]: unknown;
}

/** One batch in GET /bulk_matching/batches. */
export interface BatchSummary {
  batchId: string;
  brandId?: string;
  brandName?: string;
  /** Keycloak login id of whoever ran the upload — the only record of who owns a batch. */
  userName?: string;
  fileName?: string;
  batchStatus?: string;
  totalRows?: number;
  autoMatched?: number;
  needsReview?: number;
  reviewedTotalMatched?: number;
  failed?: number;
  createdOn?: string;
  /**
   * Set only once an import has succeeded. Null on every batch imported before the
   * audit columns existed, so treat `batchStatus === 'IMPORTED'` as the source of
   * truth for *whether* it was imported and these two as "if we know the details".
   */
  importedOn?: string | null;
  importedBy?: string | null;
}

export interface BatchesResponse {
  batches: BatchSummary[];
  total?: number;
  message?: string;
}

/** A candidate match for a queue item. */
export interface MatchSuggestion {
  id: number;
  queueId: number;
  suggestionRank: number;
  organizationId: number;
  companyName: string;
  bhiveId: string;
  country?: string;
  companyAddress?: string | null;
  score: number;
  /**
   * Org status code: -1 GHOST_MERGED, 0 INACTIVE, 1 ACTIVE, 2 GHOST, 3 HIDDEN.
   * The response also carries a separate `active` flag; the UI deliberately uses
   * status only, since it's the richer of the two.
   */
  status?: number;
  /** Human label for `status`, resolved server-side from the same enum. Null if unknown. */
  statusLabel?: string | null;
  /** Already resolved server-side (admin override if set). Null when the org has neither. */
  subscriptionType?: string | null;
  organizationType?: number;
  sourceDb?: string;
  bhiveOrg?: boolean;
  isSelected: boolean;
  createdOn?: string;
  /**
   * The candidate org's own trade IDs, joined server-side from bor_identification /
   * bor_gateway_subscription — they aren't columns on the suggestion itself. Note the
   * candidate side calls it OAR where the uploaded row calls it OSH.
   */
  higgId?: string | null;
  oarId?: string | null;
  ffcId?: string | null;
  zdhcAid?: string | null;
}

/** One uploaded row in the matching queue (shared by review + auto-matched). */
export interface MatchQueueItem {
  queueId: number;
  orgName: string;
  country?: string;
  contactEmail?: string | null;
  rowIndex: number;
  orgAddress?: string | null;
  matchingStatus: string;
  matchedId?: string | null;
  /**
   * IDs from the uploaded row — an exact hit settles matches that names leave ambiguous.
   * `bhiveId` is what the uploader typed; `matchedId` is the org the row resolved to.
   */
  bhiveId?: string | null;
  higgId?: string | null;
  zdhcAid?: string | null;
  oshId?: string | null;
  ffcId?: string | null;
  vendorId?: string | null;
  suggestions: MatchSuggestion[];
}

/** GET /bulk_matching/{batchId}/review. */
export interface ReviewResponse {
  items: MatchQueueItem[];
  batchId: string;
  totalNeedsReview: number;
}

/** GET /bulk_matching/{batchId}/auto-matched. */
export interface AutoMatchedResponse {
  items: MatchQueueItem[];
  batchId?: string;
  totalAutoMatched?: number;
}

/**
 * GET /bulk_matching/{batchId}/rows?status=… — rows by arbitrary matching status.
 * /review and /auto-matched each return a single status, so resolved rows are only
 * retrievable through this route.
 */
export interface QueueItemsResponse {
  items: MatchQueueItem[];
  batchId?: string;
  total?: number;
}

/** POST /bulk_matching/{batchId}/confirm-auto-matched. */
export interface BulkConfirmResult {
  batchId?: string;
  minScore?: number;
  confirmed: number;
  skipped?: number;
  failed?: number;
  failures?: { queueId: number; rowIndex?: number; companyName?: string; bhiveId?: string; reason?: string }[];
}

/** GET /bulk_matching/{batchId}/progress. */
export interface BatchProgress {
  batchId?: string;
  batchStatus?: string;
  fileName?: string;
  /** Keycloak login id of whoever ran the upload. */
  userName?: string;
  totalRows?: number;
  pending?: number;
  autoMatched?: number;
  needsReview?: number;
  reviewPending?: number;
  confirmed?: number;
  noMatch?: number;
  reviewedTotalMatched?: number;
  failed?: number;
  readyForDownload?: boolean;
  createdOn?: string;
  /** See {@link BatchSummary.importedOn} — null for batches imported before the audit columns. */
  importedOn?: string | null;
  importedBy?: string | null;
  error?: string;
}

/** Response of upload_excel — matching runs asynchronously afterwards. */
export const BATCH_STATUS_PROCESSING = 'PROCESSING';
export const BATCH_STATUS_COMPLETED = 'COMPLETED';
export const BATCH_STATUS_FAILED = 'FAILED';
/** An import request currently holds the batch; another one would be refused. */
export const BATCH_STATUS_IMPORTING = 'IMPORTING';
export const BATCH_STATUS_IMPORTED = 'IMPORTED';
/** Imported, but some rows were skipped and never reached the database. */
export const BATCH_STATUS_IMPORTED_WITH_ERRORS = 'IMPORTED_WITH_ERRORS';

/** Either import outcome — the batch must not be offered for import again. */
export function isImportedStatus(status: string | null | undefined): boolean {
  return status === BATCH_STATUS_IMPORTED || status === BATCH_STATUS_IMPORTED_WITH_ERRORS;
}

/** One row skipped during import, with why. */
export interface SkippedImportDetail {
  queueId: number;
  rowIndex: number;
  companyName: string | null;
  country?: string | null;
  matchingStatus: string;
  reason: string;
}

/** One row that matched an existing non-BHive org by name hash instead of creating a new one. */
export interface ReusedImportDetail {
  queueId: number;
  rowIndex: number;
  companyName: string | null;
  country?: string | null;
  existingBhiveId: string;
  action: string;
}

/** POST /bulk_matching/{batchId}/import-connections response. */
export interface ImportConnectionsResult {
  batchId: string;
  brandId: string;
  totalRowsProcessed: number;
  bhiveOrgsLinked: number;
  nonBhiveOrgsCreated: number;
  nonBhiveOrgsReused: number;
  nonBhiveOrgsReusedDetails: ReusedImportDetail[];
  relationshipsCreated: number;
  relationshipsAlreadyExisted: number;
  dataSourceFromOh: number;
  dataSourceExcelImport: number;
  skippedRows: number;
  skippedDetails: SkippedImportDetail[];
  mode: 'MERGE' | 'REPLACE';
  connectionsDeactivated: number;
  /** Rows that actually reached the database (linked + created + reused). */
  rowsImported?: number;
  /** IMPORTED, or IMPORTED_WITH_ERRORS when any row was skipped. */
  batchStatus: string;
  /**
   * Neo4j is a derived mirror of the MySQL registry, so a graph failure does NOT mean the
   * import failed — the rows are saved either way, and the backend's retry cron replays
   * whatever didn't land. PENDING_RETRY is a warning, not an error.
   */
  neo4jStatus?: 'SYNCED' | 'PENDING_RETRY' | 'DISABLED';
  neo4jSynced?: number;
  neo4jFailed?: number;
  neo4jSkipped?: number;
  neo4jError?: string | null;
}

// ── Register non-BHive brand ────────────────────────────────────────────────

/** GET /api/v1/countries — bor_country, for the country picker. */
export interface CountryOption {
  id: number;
  name: string;
  iso2?: string | null;
}

/** POST /api/v1/orgs/search body. organizationName and country are required (@NotBlank). */
export interface OrgMatchSearchRequest {
  organizationName: string;
  country: string;
  email?: string;
  phone?: string;
  website?: string;
  address?: string;
}

/** One ranked candidate from /api/v1/orgs/search (the matching engine). */
export interface OrgMatchCandidate {
  companyName: string;
  bhiveId?: string | null;
  country?: string | null;
  companyAddress?: string | null;
  websiteUrl?: string | null;
  /** 0–100. */
  score: number;
  /** 1 = brand. Only brands can be picked as the upload's brand. */
  organizationType?: number | null;
  bhiveOrg?: boolean | null;
  status?: number | null;
}

/**
 * /api/v1/orgs/search response: the input echoed back plus ranked `suggestions`,
 * or just `{ message: 'No record found' }` when nothing matches.
 */
export interface OrgMatchSearchResponse {
  suggestions?: OrgMatchCandidate[];
  message?: string;
}

/** POST /api/v1/organizations body for a new non-BHive brand. */
export interface RegisterBrandRequest {
  orgName: string;
  countryId: number;
  countryName: string;
  websiteUrl?: string;
  email?: string;
  organizationType: number;
  bhiveOrg: false;
  /** Stored in bor_address with the new org (same request, same transaction). */
  primaryAddress?: BrandAddressInput;
}

/** Address fields as in bor_address; the country is the brand's own (countryId). */
export interface BrandAddressInput {
  streetLocation?: string;
  city?: string;
  state?: string;
  pinCode?: string;
}
